/**
 * 주석 검증 결과 — 좌 목차, 가운데 미리보기, 우 지적 목록.
 * IXD 값 편집기처럼 연결·별도는 다른 문서라 상단 토글로 한쪽만 보여준다(목차·지적도 그쪽만).
 * 지적이 가리키는 셀은 미리보기에 상시 적색 배경, 셀 없이 표·목차만 가리키면 상시 적색 점선 외곽선.
 * 선택한 지적에는 실선 테두리를 건다 — 셀은 붙은 셀끼리 바깥 테두리 하나로(selected-outline).
 * 목록을 누르면 그 위치로 스크롤하고, 미리보기의 표시된 곳을 누르면 목록의 그 지적을 잡는다.
 * 위치가 없는 지적(주석 전체·위치 미확인)은 목록에만 보인다.
 */

import {
  useId,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from "react";
import { toast } from "sonner";

import type {
  FsType,
  IXDPreviewResponse,
  IxdNoteChecklistFinding,
  IxdNoteChecklistLocation,
} from "@/apis/ixd-editor/types";
import { AppMainHeader, AppPageShell } from "@/components/app-shell";
import {
  DocumentSidebar,
  useDocumentSidebarToggle,
} from "@/components/document-sidebar";
import {
  IxdPreviewRenderer,
  type IxdPreviewRendererRef,
} from "@/components/ixd-preview";
import { useScrollSpy } from "@/hooks/use-scroll-spy";
import { SidebarInset, SidebarProvider } from "@/styles/ui/sidebar";

import {
  describeLocations,
  findingIndexesAt,
  findingsForFsType,
  locationSelectors,
  nextFindingIndex,
} from "./finding-location";
import { FindingsPanel } from "./findings-panel";
import { chunksOf, FsTypeSwitch } from "./fs-type-switch";
import { selectedOutlineCss } from "./selected-outline";
import { buildTocCategories } from "./toc-categories";

const hasCells = (location: IxdNoteChecklistLocation) =>
  location.cellIds.length > 0;

const CELL_MARK_CSS =
  "background-color:color-mix(in srgb,var(--status-negative) 12%,transparent)!important;cursor:pointer";
// 표·목차 블록에 배경을 칠하면 그 안 셀이 전부 지적된 셀처럼 보인다. 블록은 외곽선으로 표시한다.
const BLOCK_MARK_CSS =
  "outline:1px dashed var(--status-negative)!important;outline-offset:4px;cursor:pointer";

/** chunk 는 content-visibility:auto 라 스크롤 중 높이가 펴지며 목표가 밀린다. 멈출 때까지 다시 맞춘다. */
const MAX_ALIGN_FRAMES = 12;

const scrollIntoViewSettled = (target: HTMLElement) => {
  // 셀은 가운데로, 표·목차는 머리가 보이게 위로 맞춘다 (긴 표를 가운데에 두면 제목이 밀려난다).
  const block = target.matches("td, th") ? "center" : "start";
  let previousTop = Number.NaN;
  let attempts = 0;
  const align = () => {
    target.scrollIntoView({ block });
    const top = target.getBoundingClientRect().top;
    if (top !== previousTop && ++attempts < MAX_ALIGN_FRAMES) {
      previousTop = top;
      window.requestAnimationFrame(align);
    }
  };
  align();
};

interface NoteChecklistViewProps {
  preview: IXDPreviewResponse;
  findings: IxdNoteChecklistFinding[];
}

export const NoteChecklistView = ({
  preview,
  findings: allFindings,
}: NoteChecklistViewProps) => {
  const rendererRef = useRef<IxdPreviewRendererRef | null>(null);
  // 미리보기는 렌더러 안쪽 div 가 스크롤한다 — 스크롤 스파이가 그 div 를 보게 ref 로 옮겨 둔다.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // 테두리 CSS 는 DOM 격자를 읽어 만들므로 선택할 때 같이 계산해 둔다.
  const [selected, setSelected] = useState<{
    index: number;
    outlineCss: string;
  } | null>(null);
  const selectedIndex = selected?.index ?? null;
  const scopeId = `note-checklist-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const sidebar = useDocumentSidebarToggle();

  const availableFsTypes = (["consolidated", "separate"] as const).filter(
    (fsType) => chunksOf(preview, fsType).length > 0,
  );
  const [fsType, setFsType] = useState<FsType>(
    availableFsTypes[0] ?? "consolidated",
  );
  const chunks = chunksOf(preview, fsType);
  // 선택 index 는 이 목록 기준이다 — 유형을 바꾸면 선택을 비운다.
  const findings = findingsForFsType(allFindings, fsType);
  const [activeChunkId, setActiveChunkId] = useState(chunks[0]?.id ?? null);

  const handleFsTypeChange = (next: FsType) => {
    setFsType(next);
    setSelected(null);
    setActiveChunkId(chunksOf(preview, next)[0]?.id ?? null);
  };
  useScrollSpy({
    scrollRef,
    orderedIds: chunks.map((chunk) => chunk.id),
    onActiveChange: setActiveChunkId,
    getElement: (id, root) =>
      root.querySelector(`[data-chunk-id="${CSS.escape(id)}"]`),
  });

  const selectorsByFinding = findings.map((f) =>
    locationSelectors(f.locations),
  );
  const ruleFor = (selectors: string[], declarations: string) =>
    selectors.length > 0
      ? `${selectors.map((s) => `#${scopeId} ${s}`).join(",")}{${declarations}}`
      : "";
  const markSelectors = (byCell: boolean) =>
    findings.flatMap((f) =>
      locationSelectors(f.locations.filter((l) => hasCells(l) === byCell)),
    );
  const highlightCss =
    ruleFor(markSelectors(true), CELL_MARK_CSS) +
    ruleFor(markSelectors(false), BLOCK_MARK_CSS) +
    (selected?.outlineCss ?? "");

  const select = (index: number, finding: IxdNoteChecklistFinding) => {
    const container = scrollRef.current;
    setSelected({
      index,
      outlineCss: container
        ? selectedOutlineCss(container, scopeId, finding.locations)
        : "",
    });
  };

  // 위치가 여러 곳이면 고른 위치로, 카드 자체를 누르면 첫 위치로 간다.
  const handleFindingSelect = (
    index: number,
    finding: IxdNoteChecklistFinding,
    locationIndex = 0,
  ) => {
    select(index, finding);
    const location = finding.locations[locationIndex];
    const target =
      location &&
      scrollRef.current?.querySelector<HTMLElement>(
        locationSelectors([location]).join(","),
      );
    if (!target) {
      toast.error("미리보기에서 이 위치를 찾지 못했습니다.");
      return;
    }
    scrollIntoViewSettled(target);
  };

  const handlePreviewClick = (e: MouseEvent<HTMLElement>) => {
    if (!(e.target instanceof Element)) return;
    const next = nextFindingIndex(
      findingIndexesAt(e.target, selectorsByFinding),
      selectedIndex,
    );
    const finding = next === undefined ? undefined : findings[next];
    if (next === undefined || !finding) return;
    select(next, finding);
    listRef.current
      ?.querySelector(`[data-finding-index="${next}"]`)
      ?.scrollIntoView({ block: "nearest" });
  };

  return (
    <AppPageShell
      header={
        <AppMainHeader
          sidebar={sidebar.headerToggle}
          center={
            <FsTypeSwitch
              value={fsType}
              availableFsTypes={availableFsTypes}
              onChange={handleFsTypeChange}
            />
          }
        />
      }
    >
      <SidebarProvider
        open={sidebar.open}
        onOpenChange={sidebar.setOpen}
        className="h-full min-h-0"
        style={
          {
            "--sidebar-width": "192px",
            "--sidebar-width-icon": "3rem",
            minHeight: 0,
          } as CSSProperties
        }
      >
        <DocumentSidebar
          variant="flat"
          collapsible="offcanvas"
          showLogoAndToggle={false}
          peek={sidebar.peek}
          categories={buildTocCategories(
            chunks,
            fsType,
            selected
              ? findings.slice(selected.index, selected.index + 1)
              : findings,
          )}
          activeSection={activeChunkId}
          onSectionClick={(section) => {
            setActiveChunkId(section.id);
            rendererRef.current?.scrollToChunk(section.id);
          }}
        />

        <SidebarInset className="flex min-w-0 flex-row">
          <main
            id={scopeId}
            className="relative min-w-0 flex-1"
            onClick={handlePreviewClick}
          >
            {highlightCss && <style>{highlightCss}</style>}
            <IxdPreviewRenderer
              // 연결/별도는 다른 문서다 — 바꾸면 새로 마운트해 스크롤을 맨 위에서 시작한다.
              key={fsType}
              ref={(handle) => {
                rendererRef.current = handle;
                scrollRef.current = handle?.getScrollContainer() ?? null;
              }}
              chunks={chunks}
            />
          </main>
          <FindingsPanel
            findings={findings}
            selectedIndex={selectedIndex}
            onSelect={handleFindingSelect}
            describeLocation={describeLocations(chunks)}
            listRef={listRef}
          />
        </SidebarInset>
      </SidebarProvider>
    </AppPageShell>
  );
};
